import * as React from 'react';
import { Filter, X, Plus } from 'lucide-react';
import { cn } from '../lib/utils';

export interface FilterChipDef {
  id: string;
  label: string;
  /** Currently-applied value rendered in the chip ("running", "Line 3", "2024-01-04") */
  value?: React.ReactNode;
  /** Render a small popover when chip is clicked. */
  renderEditor?: (close: () => void) => React.ReactNode;
  onClear?: () => void;
}

export interface FilterBarProps {
  /** Active filter chips. */
  filters: FilterChipDef[];
  /** "Add filter" menu options that don't yet have an active chip. */
  available?: Array<{ id: string; label: string; onSelect: () => void }>;
  /** Optional saved-views slot — render a dropdown of named view presets. */
  savedViewsSlot?: React.ReactNode;
  /** Right-side action area — search input, sort, export, etc. */
  rightSlot?: React.ReactNode;
  className?: string;
}

/**
 * Tyndo FilterBar — chip-style active filters + "Add filter" menu.
 *
 * Replaces every module's bespoke "Equipment / Priority / Status select" stack.
 * Active filter values render as removable chips; an inline editor popover
 * appears when the chip is clicked. Available-but-not-active filters appear in
 * a "+ Add filter" dropdown.
 */
export function FilterBar({
  filters,
  available,
  savedViewsSlot,
  rightSlot,
  className,
}: FilterBarProps) {
  const [addOpen, setAddOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setAddOpen(false);
        setEditingId(null);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex flex-wrap items-center gap-2 border-b border-border bg-surface-raised px-4 py-2',
        className
      )}
    >
      <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      {savedViewsSlot}

      {filters.length === 0 && !available?.length && (
        <span className="text-xs text-muted-foreground">No filters applied</span>
      )}

      {filters.map((f) => (
        <div key={f.id} className="relative">
          <button
            type="button"
            onClick={() =>
              setEditingId((prev) => (prev === f.id ? null : f.id))
            }
            className="group inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-sunken px-2 py-1 text-xs transition-colors hover:bg-accent"
          >
            <span className="text-muted-foreground">{f.label}:</span>
            <span className="font-medium text-foreground">{f.value ?? '—'}</span>
            {f.onClear && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  f.onClear?.();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    f.onClear?.();
                  }
                }}
                className="ml-1 -mr-0.5 rounded p-0.5 text-muted-foreground hover:bg-foreground/10 hover:text-foreground"
                aria-label={`Clear ${f.label}`}
              >
                <X className="h-3 w-3" />
              </span>
            )}
          </button>
          {editingId === f.id && f.renderEditor && (
            <div className="absolute left-0 top-full z-30 mt-1 w-72 rounded-lg border border-border bg-surface-overlay p-3 shadow-[var(--shadow-md)]">
              {f.renderEditor(() => setEditingId(null))}
            </div>
          )}
        </div>
      ))}

      {available && available.length > 0 && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setAddOpen((v) => !v)}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <Plus className="h-3 w-3" />
            Add filter
          </button>
          {addOpen && (
            <div className="absolute left-0 top-full z-30 mt-1 w-52 overflow-hidden rounded-lg border border-border bg-surface-overlay shadow-[var(--shadow-md)]">
              {available.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    a.onSelect();
                    setAddOpen(false);
                  }}
                  className="block w-full px-3 py-1.5 text-left text-sm text-foreground hover:bg-accent"
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {rightSlot && <div className="ml-auto flex items-center gap-2">{rightSlot}</div>}
    </div>
  );
}
